import {clone, isEmpty} from 'ramda';

import {CorrectionType} from '@dms/api/shared';
import {
  PutReceiveNoteCorrectionItemCalculationResponse,
  ReceiveNoteCorrectionItemChangedField,
  usePutReceiveNoteCorrectionItemCalculationMutation,
} from '@dms/api/warehouse';
import {useFormRecalculation} from '@dms/warehouse/shared-components';

import {sanitizeObject} from 'shared/web';

import {ReceiveNoteCorrectionItemForm} from '../types/ReceiveNoteCorrectionItemForm';

interface UseReceiveNoteCorrectionItemFormRecalculationConfig {
  receiveNoteCorrectionItemId: string;
}

/**
 * Custom hook for handling receive note correction item form recalculation with proper type handling
 * for the receive note correction item calculation mutation. This is a specialized wrapper around the
 * generic useFormRecalculation hook.
 */
export function useReceiveNoteCorrectionItemFormRecalculation(
  config: UseReceiveNoteCorrectionItemFormRecalculationConfig
) {
  const [calculatePrices, {isLoading: isCalculateLoading}] =
    usePutReceiveNoteCorrectionItemCalculationMutation();

  const formRecalculation = useFormRecalculation<
    ReceiveNoteCorrectionItemForm,
    {
      changedFieldValue: {
        field: ReceiveNoteCorrectionItemChangedField;
        correctionType: CorrectionType;
      };
      currentFeFieldsValues: {
        receiveItem: {
          receiveAfterQuantity: number;
          receiveAfterUnitPrice: number;
          receiveAfterStockValue: number;
          receiveBeforeQuantity: number;
          receiveBeforeUnitPrice: number;
          receiveBeforeStockValue: number;
        };
        deliveryItem: {
          deliveryAfterQuantity: number;
          deliveryAfterUnitPrice: number;
          deliveryAfterStockValue: number;
          deliveryBeforeQuantity: number;
          deliveryBeforeUnitPrice: number;
          deliveryBeforeStockValue: number;
        };
        supplierArticle: {
          supplierQuantity: number;
          warehouseQuantity: number;
        };
      };
    },
    PutReceiveNoteCorrectionItemCalculationResponse
  >({
    allowedFields: ALLOWED_VALUES,
    allowedObjects: ALLOWED_VALUES_IN_OBJECT,
    recalculateMutation: (params) => ({
      unwrap: () =>
        calculatePrices({
          body: params.body,
          signal: params.signal,
        }).unwrap(),
    }),
    requestConfig: {
      receiveNoteCorrectionItemId: config.receiveNoteCorrectionItemId,
    },
    buildRequestBody: (values, changedField) => ({
      changedFieldValue: {
        field: changedField as ReceiveNoteCorrectionItemChangedField,
        correctionType: values.correctionType as CorrectionType,
      },
      currentFeFieldsValues: {
        receiveItem: {
          receiveAfterQuantity: values.receiveItem?.receiveAfterQuantity ?? 0,
          receiveAfterUnitPrice: values.receiveItem?.receiveAfterUnitPrice ?? 0,
          receiveAfterStockValue: values.receiveItem?.receiveAfterStockValue ?? 0,
          receiveBeforeQuantity: values.receiveItem?.receiveBeforeQuantity ?? 0,
          receiveBeforeUnitPrice: values.receiveItem?.receiveBeforeUnitPrice ?? 0,
          receiveBeforeStockValue: values.receiveItem?.receiveBeforeStockValue ?? 0,
        },
        deliveryItem: {
          deliveryAfterQuantity: values.deliveryItem?.deliveryAfterQuantity ?? 0,
          deliveryAfterUnitPrice: values.deliveryItem?.deliveryAfterUnitPrice ?? 0,
          deliveryAfterStockValue: values.deliveryItem?.deliveryAfterStockValue ?? 0,
          deliveryBeforeQuantity: values.deliveryItem?.deliveryBeforeQuantity ?? 0,
          deliveryBeforeUnitPrice: values.deliveryItem?.deliveryBeforeUnitPrice ?? 0,
          deliveryBeforeStockValue: values.deliveryItem?.deliveryBeforeStockValue ?? 0,
        },
        supplierArticle: {
          supplierQuantity: values.supplierArticle?.supplierQuantity ?? 0,
          warehouseQuantity: values.supplierArticle?.warehouseQuantity ?? 0,
        },
      },
    }),
    transformResponse: (calculatedPrices, values) =>
      clone(
        sanitizeObject({
          ...values,
          receiveItem: calculatedPrices.receiveItem,
          deliveryItem: calculatedPrices.deliveryItem,
          supplierArticle: {
            supplierQuantity: calculatedPrices?.supplierArticle?.supplierQuantity,
            supplierUnitId: values?.supplierArticle?.supplierUnitId,
            warehouseQuantity: calculatedPrices?.supplierArticle?.warehouseQuantity,
            warehouseUnitId: values?.supplierArticle?.warehouseUnitId,
            supplierOrderingNumber: values?.supplierArticle?.supplierOrderingNumber,
            supplierBulkPackageQuantity: values?.supplierArticle?.supplierBulkPackageQuantity,
            supplier: values?.supplierArticle?.supplier,
          },
        })
      ),
    isDifferenceEmpty: (allowedDifference) => {
      const isReceiveNoteItemPricesDiffEmpty = allowedDifference?.receiveItem
        ? isEmpty(allowedDifference?.receiveItem)
        : false;
      const isDeliveryItemPricesDiffEmpty = allowedDifference?.deliveryItem
        ? isEmpty(allowedDifference?.deliveryItem)
        : false;

      return isReceiveNoteItemPricesDiffEmpty && isDeliveryItemPricesDiffEmpty;
    },
  });

  return {
    ...formRecalculation,
    isCalculateLoading,
  };
}

const ALLOWED_VALUES_IN_OBJECT = ['receiveItem', 'deliveryItem'];

// nested keys from ALLOWED_VALUES_IN_OBJECT
const ALLOWED_VALUES = [
  'receiveAfterQuantity',
  'deliveryAfterQuantity',
  'receiveAfterStockValue',
  'deliveryAfterStockValue',
];
